import { FC, useContext, useEffect, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import { Collaborator } from '@components/AssignCollaboratorsToAttachmentForm';
import CommentAttachment from '@components/CommentAttachment';
import CommentParticipants from '@components/Message/CommentParticipants';
import Buttons from '@components/Buttons';
import { ArrowBendDownRight } from '@phosphor-icons/react';
import * as format from '@format';
import FollowupHistoryModal from '@modals/FollowupHistoryModal';
import NoodleAI from '@components/Icons/NoodleAI';
import dashboardContext from '@layouts/DashboardLayout/DashboardContext';
import isTaskSkipped from '@helpers/isTaskSkipped';
import classes from './UserWorkflowSteps.module.scss';

type Data = Pick<ApiModels.MessageAttachment, 'id' | 'referenceId' | 'canAccess' | 'title' | 'nextFollowupTime' | 'isFollowupEnabled' | 'lastActivityAt' | 'completedAt' | 'integrationSlugs'> &
  ApiModels.MessageAttachmentWithTypedData & {
    assigneeIds: string[];
  };

const Attachment: FC<{
  data: Data;
  onChange: () => Promise<void>;
  creator: Pick<ApiModels.Creator, 'slug' | 'personId'>;
  userWorkflowId: string;
  collaborators: Collaborator[];
  setAssignCollaboratorModalOpen: (open: boolean) => void;
  setSelectedAttachment: (attachment: Data) => void;
  userId?: string;
  canAssignCollaborators: boolean | null;
  setFulfilledAttachments?: (attachments: string[]) => void;
  fulfilledAttachments?: string[];
  customerId: string;
}> = ({
  data,
  onChange,
  creator,
  userWorkflowId,
  collaborators,
  setAssignCollaboratorModalOpen,
  setSelectedAttachment,
  userId,
  canAssignCollaborators,
  setFulfilledAttachments,
  fulfilledAttachments,
  customerId,
}) => {
  const [isAssignedToCurrentUser, setIsAssignedToCurrentUser] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isFulfilled, setFulfilled] = useState<boolean | null>(null);
  const [inReview, setInReview] = useState<boolean | null>(null);
  const [isFailed, setFailed] = useState<boolean | null>(null);
  const { isInDashboard } = useContext(dashboardContext);
  const [isSkipped, setIsSkipped] = useState<boolean>(false);
  const [overrideIsAssigned, setOverrideIsAssigned] = useState<boolean>(false);

  const isCustomer = userId === customerId;

  useEffect(() => {
    setIsAssignedToCurrentUser((data.assigneeIds?.includes(userId || '') && isFulfilled === false && !isTaskSkipped(data) && !inReview) || false);
  }, [data, userId, isFulfilled, inReview]);

  useEffect(() => {
    if ((isFulfilled || isSkipped) && setFulfilledAttachments && fulfilledAttachments && !fulfilledAttachments.includes(data.id)) {
      setFulfilledAttachments([...fulfilledAttachments, data.id]);
    }
  }, [isFulfilled, fulfilledAttachments, data.id, isSkipped, setFulfilledAttachments]);

  if (isSkipped && !isInDashboard) return null;

  return (
    <>
      <div
        className={classes.message__attachment}
        style={{
          ...((data.canAccess && isAssignedToCurrentUser && !overrideIsAssigned && { border: '4px solid var(--color-warning)', borderRadius: 16 }) || undefined),
          ...((!data.canAccess && !isAssignedToCurrentUser && { border: '4px solid var(--color-gray-25)', borderRadius: 16 }) || undefined),
          ...((data.canAccess && isFailed && { border: '4px solid var(--color-error)', borderRadius: 16 }) || undefined),
        }}
      >
        <div
          className={classes.message__attachment__updatedAt}
          style={
            ((isAssignedToCurrentUser && data.canAccess) && { backgroundColor: 'var(--color-warning)', color: 'var(--color-gray-0)', paddingBottom: 7 })
            || (!data.canAccess && !isAssignedToCurrentUser && { color: 'var(--color-gray-75)', paddingBottom: 7 })
            || undefined
          }
        >
          {(isAssignedToCurrentUser && data.canAccess && !overrideIsAssigned) ? <b>You are assigned</b> : null}
          {!data.canAccess && <b>This task is not for you</b>}
        </div>
        <CommentAttachment
          setIsFulfilled={setFulfilled}
          setIsFailed={setFailed}
          setInReview={setInReview}
          attachment={data}
          creator={creator}
          userWorkflowId={userWorkflowId}
          isWorkflowAttachment
          reload={onChange}
          setIsSkipped={setIsSkipped}
          overrideIsAssigned={setOverrideIsAssigned}
        />
        {!isFulfilled && (collaborators.filter(c => data.assigneeIds?.includes(c.id)).length > 0
          || (canAssignCollaborators && (isCustomer ? isAssignedToCurrentUser : true))) && !isTaskSkipped(data) && (
          <div className={classes.message__attachment__assignments}>
            <div>
              <CommentParticipants
                userLogoSize={20}
                participantsToHighlight={data.assigneeIds?.filter(id => id === userId)}
                remainingParticipantsAsNumber
                participants={collaborators.filter(c => data.assigneeIds?.includes(c.id))}
                maxLength={2}
                areAssignees
              />
            </div>
            {canAssignCollaborators && (isCustomer ? isAssignedToCurrentUser : true) && (
              <Buttons
                isWrapper
                onClick={() => {
                  setSelectedAttachment(data);
                  setAssignCollaboratorModalOpen(true);
                }}
              >
                {isAssignedToCurrentUser
                  ? (
                    <>Reassign</>
                  )
                  : (
                    <>Assign {collaborators.filter(c => data.assigneeIds?.includes(c.id)).length === 0 ? 'collaborators' : ''}</>
                  )}
              </Buttons>
            )}
          </div>
        )}
      </div>
      {data.isFollowupEnabled && !isFulfilled && data.nextFollowupTime && (new Date(data.nextFollowupTime) > new Date()) && isInDashboard && (
        <Buttons isWrapper onClick={() => setModalOpen(true)}>
          <div className={classes.followupButton}>
            <div className={classes.left}>
              <ArrowBendDownRight size={16} />
              <div>
                <p className="caption-bold">Following up</p>
                <p className="caption">in {format.datetimeLocalTZ.timeSince({ date: data.nextFollowupTime })}</p>
              </div>
            </div>
            <NoodleAI size={16} />
          </div>
        </Buttons>
      )}
      {isModalOpen && (
        <FollowupHistoryModal
          attachmentId={data.id}
          assignees={collaborators.filter(c => data.assigneeIds?.includes(c.id))}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default Attachment;
